import { useState, useEffect } from 'react';
import { Download, Smartphone, X, Share, PlusSquare, MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import { toast } from 'sonner';

const INSTALL_PROMPT_KEY = 'pwa_install_prompted';

export function PWAInstallPrompt() {
  const { isInstalled, isInstallable, isIOSDevice, canPromptInstall, promptInstall } = usePWAInstall();
  const [showModal, setShowModal] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    // Don't show if already installed
    if (isInstalled) return;
    
    // Don't show if already prompted this session
    if (sessionStorage.getItem(INSTALL_PROMPT_KEY)) return;

    // Show after a delay to not overwhelm user
    const timer = setTimeout(() => {
      if (isIOSDevice) {
        setShowIOSGuide(true);
        setShowModal(true);
      } else if (isInstallable || canPromptInstall) {
        setShowModal(true);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [isInstalled, isInstallable, isIOSDevice, canPromptInstall]);

  const handleInstall = async () => {
    if (canPromptInstall) {
      const success = await promptInstall();
      if (success) {
        toast.success('Application installée !');
      }
    }
    setShowModal(false);
    sessionStorage.setItem(INSTALL_PROMPT_KEY, '1');
  };

  const handleSkip = () => {
    setShowModal(false);
    sessionStorage.setItem(INSTALL_PROMPT_KEY, '1');
  };

  if (isInstalled) return null;

  return (
    <Dialog open={showModal} onOpenChange={(v) => !v && handleSkip()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5 text-primary" />
            Installer l'application
          </DialogTitle>
          <DialogDescription>
            Accédez plus rapidement à votre conciergerie depuis votre écran d'accueil.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-3">
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Smartphone className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="font-medium text-foreground">Accès instantané</p>
              <p className="text-xs">Lancez l'app en un tap</p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <span className="text-primary">🔔</span>
            </div>
            <div>
              <p className="font-medium text-foreground">Notifications</p>
              <p className="text-xs">Recevez les réponses instantanément</p>
            </div>
          </div>
        </div>

        {showIOSGuide ? (
          <IOSInstallGuide onDismiss={handleSkip} />
        ) : (
          <div className="flex flex-col gap-2">
            <Button onClick={handleInstall} className="w-full">
              <Download className="w-4 h-4 mr-2" />
              Installer
            </Button>
            <Button variant="ghost" onClick={handleSkip} className="w-full text-muted-foreground text-sm">
              Plus tard
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function IOSInstallGuide({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div className="space-y-4">
      <div className="bg-muted/50 rounded-lg p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
            <Share className="w-4 h-4 text-blue-500" />
          </div>
          <div>
            <p className="text-sm font-medium">1. Appuyez sur Partager</p>
            <p className="text-xs text-muted-foreground">Le bouton en bas de Safari</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
            <PlusSquare className="w-4 h-4 text-blue-500" />
          </div>
          <div>
            <p className="text-sm font-medium">2. "Sur l'écran d'accueil"</p>
            <p className="text-xs text-muted-foreground">Faites défiler si nécessaire</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
            <span className="text-green-500">✓</span>
          </div>
          <div>
            <p className="text-sm font-medium">3. Appuyez sur Ajouter</p>
            <p className="text-xs text-muted-foreground">L'app apparaît sur votre écran</p>
          </div>
        </div>
      </div>
      <Button onClick={onDismiss} className="w-full">J'ai compris</Button>
    </div>
  );
}

// Composant pour le profil - Guide d'installation
export function PWAInstallGuide() {
  const { isInstalled, isIOSDevice, canPromptInstall, promptInstall } = usePWAInstall();

  const handleInstall = async () => {
    if (canPromptInstall) {
      const success = await promptInstall();
      if (success) {
        toast.success('Application installée !');
      }
    }
  };

  if (isInstalled) {
    return (
      <div className="flex items-center gap-3 p-4 bg-green-500/10 rounded-lg">
        <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
          <span className="text-green-600">✓</span>
        </div>
        <div>
          <p className="text-sm font-medium text-green-600">Application installée</p>
          <p className="text-xs text-green-600/70">Vous utilisez la version optimisée</p>
        </div>
      </div>
    );
  }

  // Chrome/Edge with native install
  if (canPromptInstall) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-3">
            <Download className="w-5 h-5 text-primary" />
            <div>
              <p className="text-sm font-medium">Installer l'application</p>
              <p className="text-xs text-muted-foreground">Accès rapide depuis l'écran d'accueil</p>
            </div>
          </div>
          <Button size="sm" onClick={handleInstall}>
            Installer
          </Button>
        </div>
      </div>
    );
  }

  // iOS instructions
  if (isIOSDevice) {
    return (
      <div className="space-y-3">
        <div className="p-4 bg-muted/50 rounded-lg">
          <p className="text-sm font-medium mb-3 flex items-center gap-2">
            <Download className="w-4 h-4 text-primary" />
            Installer sur iPhone/iPad
          </p>
          <div className="space-y-2 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Share className="w-4 h-4 text-blue-500" />
              <span>1. Appuyez sur <strong>Partager</strong> (📤)</span>
            </div>
            <div className="flex items-center gap-2">
              <PlusSquare className="w-4 h-4 text-blue-500" />
              <span>2. Choisissez <strong>"Sur l'écran d'accueil"</strong></span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 text-green-500">✓</span>
              <span>3. Appuyez sur <strong>Ajouter</strong></span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Other browsers (Firefox, etc.) - generic instructions
  return (
    <div className="space-y-3">
      <div className="p-4 bg-muted/50 rounded-lg">
        <p className="text-sm font-medium mb-3 flex items-center gap-2">
          <Download className="w-4 h-4 text-primary" />
          Installer l'application
        </p>
        <div className="space-y-2 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <MoreVertical className="w-4 h-4" />
            <span>1. Ouvrez le menu du navigateur (⋮)</span>
          </div>
          <div className="flex items-center gap-2">
            <Download className="w-4 h-4" />
            <span>2. Cherchez <strong>"Installer l'application"</strong></span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 text-green-500">✓</span>
            <span>3. Confirmez l'installation</span>
          </div>
        </div>
      </div>
    </div>
  );
}
